/**
 * 术语表处理器的类型定义
 */
export namespace GlossaryHelperTypes {
  /**
   * 术语项接口
   */
  export interface GlossaryTerm {
    /** 源语言术语 */
    from: string
    /** 目标语言术语 */
    to: string
    /** 匹配选项 */
    options?: {
      /** 是否区分大小写，默认为true */
      caseSensitive?: boolean
      /** 是否仅匹配完整单词，默认为true */
      wholeWord?: boolean
      /** 优先级，数字越大优先级越高，默认为0 */
      priority?: number
    }
  }

  /**
   * 术语表接口
   */
  export interface GlossaryData {
    /** 源语言代码 */
    fromLang: string
    /** 目标语言代码 */
    toLang: string
    /** 术语列表 */
    // list: GlossaryTerm[]
    fromText: string
    toText: string
    termId: number
  }

  /**
   * 占位符信息
   */
  export interface PlaceholderInfo {
    /** 占位符标记，如 <t>1</t> */
    placeholder: string
    /** 原始术语 */
    originalTerm: string
    /** 目标术语 */
    targetTerm: string
    /** 在原文中的位置信息 */
    position: {
      start: number
      end: number
    }
    /** 术语项的索引 */
    termIndex: number
    /** 术语ID */
    termId: number
  }

  /**
   * 文本预处理结果
   */
  export interface PreprocessResult {
    /** 处理后的文本（包含占位符） */
    processedText: string
    /** 占位符映射信息 */
    placeholders: PlaceholderInfo[]
    /** 是否发生了术语替换 */
    hasReplacements: boolean
  }

  /**
   * 文本后处理结果
   */
  export interface PostprocessResult {
    /** 最终处理后的文本 */
    finalText: string
    /** 成功恢复的占位符数量 */
    recoveredCount: number
    /** 处理过程中的错误信息 */
    errors: string[]
  }

  /**
   * 术语匹配配置
   */
  export interface MatchConfig {
    /** 是否区分大小写，默认为true */
    caseSensitive?: boolean
    /** 是否仅匹配完整单词，默认为true */
    wholeWord?: boolean
    /** 最大替换次数，默认为无限制 */
    maxReplacements?: number
    /** 冲突解决策略，默认为 'longest' */
    conflictResolution?: 'longest' | 'first' | 'skip'
    /** 冲突检测阈值，默认为1000 */
    conflictDetectionThreshold?: number
  }

  /**
   * 正则表达式缓存项
   */
  export interface RegexCacheItem {
    /** 正则表达式对象 */
    regex: RegExp
    /** 最后使用时间戳 */
    lastUsed: number
    /** 使用次数 */
    useCount: number
  }

  /**
   * 性能配置选项
   */
  export interface PerformanceConfig {
    /** 正则表达式缓存大小限制，默认为100 */
    regexCacheSize?: number
    /** 文本长度阈值，低于此值使用简化处理，默认为100 */
    shortTextThreshold?: number
    /** 是否启用性能监控，默认为false */
    enablePerformanceMonitoring?: boolean
    /** 是否启用缓存统计，默认为false */
    enableCacheStats?: boolean
  }

  /**
   * 性能统计信息
   */
  export interface PerformanceStats {
    /** 缓存命中次数 */
    cacheHits: number
    /** 缓存未命中次数 */
    cacheMisses: number
    /** 缓存命中率 */
    cacheHitRate: number
    /** 平均处理时间（毫秒） */
    averageProcessingTime: number
    /** 总处理次数 */
    totalProcessingCount: number
  }

  /**
   * 术语表处理器配置
   */
  export interface ProcessorConfig {
    /** 占位符前缀，默认为 't' */
    placeholderPrefix?: string
    /** 匹配配置 */
    matchConfig?: MatchConfig
    /** 是否启用调试模式，默认为false */
    debug?: boolean
    /** 性能配置选项 */
    performance?: PerformanceConfig
  }

  /**
   * 错误类型枚举
   */
  export enum ErrorType {
    /** 未找到匹配的术语表 */
    NO_GLOSSARY_FOUND = 'NO_GLOSSARY_FOUND',
    /** 无效的术语数据 */
    INVALID_TERM_DATA = 'INVALID_TERM_DATA',
    /** 占位符解析失败 */
    PLACEHOLDER_PARSE_ERROR = 'PLACEHOLDER_PARSE_ERROR',
    /** 术语冲突 */
    TERM_CONFLICT = 'TERM_CONFLICT',
  }

  /**
   * 处理错误接口
   */
  export interface ProcessError {
    type: ErrorType
    message: string
    details?: any
  }
}
